import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TitleBar } from 'styles/layout/titlebar';
import { WPSForm, WPSLabel, WPSInput, ErrorMsg } from 'styles/layout/forms';
import { WPSButton } from 'styles/layout/buttons';
import { setGlobalErrorMsg, setGlobalSuccessMsg } from 'store/global/globalActions';
import { updateWebsiteWPDefines } from 'store/website/websiteActions';
import { Row } from 'styles/layout/grid';
import Modal from 'components/layout/modal';
import { useForm } from 'react-hook-form';
import FormHelper from 'helpers/form';
import JsxHelper from 'helpers/jsx';
import StringHelper from 'helpers/string';
import ArrayHelper from 'helpers/array';
import { updateGlobalWPDefines } from 'store/setting/settingActions';
import { Container } from 'styles/website/profile';
import { useSelector } from 'react-redux';
import { globalWPDefines } from 'store/setting/settingSelectors';
import WPSDataTable from 'components/wpstaq/WPSDataTable/WPSDataTable';
import useModal from '../../../hooks/useModal';
import { isEmpty } from 'helpers';
import WebsiteHelper from 'helpers/website';

const WPDefines = ({ website }) => {
  const dispatch = useDispatch();
  const modalDialog = useModal();
  const { register, errors, handleSubmit } = useForm({ reValidateMode: 'onSubmit' });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [defines, setDefines] = useState([]);
  const [details, setDetails] = useState({ name: '', value: '' });
  const [updateMode, setUpdateMode] = useState(false);
  const [updatedItemSlug, setUpdatedItemSlug] = useState('');
  const currentGlobalDefines = useSelector(globalWPDefines);
  const [globalDefinesFlag, setGlobalDefinesFlag] = useState(false);
  const [isGlobalPage, setIsGlobalPage] = useState(false);
  const scope = website ? 'private' : 'global';

  useEffect(() => {
    if (scope === 'private') {
      setDefines(WebsiteHelper.getWPDefines(website));
    } else {
      setDefines(currentGlobalDefines || []);
      setGlobalDefinesFlag(true);
    }
    if (window.location.pathname.includes('settings')) {
      setIsGlobalPage(true);
    }
    // eslint-disable-next-line
  }, []);

  const onSubmit = () => {
    setLoading(true);
    if (scope === 'private') {
      const data = {
        website_slug: website.slug,
        defines: defines.map(o => ({ name: o.name, value: o.value, type: 'auto', slug: o.slug })),
      };
      dispatch(updateWebsiteWPDefines(data))
        .then(() =>
          dispatch(
            setGlobalSuccessMsg({
              model: 'WP config file',
              onId: WebsiteHelper.getLabel(website),
              action: 'updated',
              plural: false,
            }),
          ),
        )
        .catch(err => dispatch(setGlobalErrorMsg(err)))
        .finally(() => setLoading(false));
    } else if (scope === 'global' || globalDefinesFlag) {
      const data = {
        defines: defines.map(o => ({ name: o.name, value: o.value, type: 'auto', slug: o.slug })),
      };
      dispatch(updateGlobalWPDefines(data))
        .then(() =>
          dispatch(
            setGlobalSuccessMsg({ model: 'WP config file', action: 'updated', plural: false }),
          ),
        )
        .catch(err => dispatch(setGlobalErrorMsg(err)))
        .finally(() => setLoading(false));
    }
  };

  const onChange = e => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  const onAdd = () => {
    const data = { ...details, slug: StringHelper.randomString() };
    setDefines(prev => [...prev, data]);
    setDetails({ name: '', value: '' });
    setModal(false);
  };

  const onEdit = item => {
    let myDefines = [...defines];
    let index = myDefines.findIndex(el => el.slug === updatedItemSlug);
    if (index !== -1) {
      myDefines[index].name = item.name;
      myDefines[index].value = item.value;
    }
    setDefines(myDefines);
    setUpdateMode(false);
    setModal(false);
    setDetails({ name: '', value: '' });
    setUpdatedItemSlug('');
  };

  const handleItemEdit = item => {
    const { name, value, slug } = item;
    setUpdatedItemSlug(slug);
    setDetails({ name: name, value: value });
    setModal(true);
  };

  const buildPreview = () => {
    let lines = [];
    lines.push('<?php');
    
    lines.push('\r\n');
    lines.push('// ...some code before your auto-generated constants.');
    lines.push('\r\n');

    for (const define of defines) {
      const name = define.name;
      let value = define.value;
      if (StringHelper.is(value)) {
        if (!['true', 'false'].includes(value)) {
          value = `'${value}'`;
        }
      }
      lines.push(`if (!defined('${name}')) {`);
      lines.push(`  define( '${name}', ${value} );`);
      lines.push(`}`)
    }

    lines.push('\r\n');
    lines.push('// ...some code after your auto-generated constants.');
    lines.push('\r\n');

    return lines.join('\r\n');
  }

  const handlePreviewFileContent = () => {
    modalDialog({
      editorMode: true,
      editorConfig: {
        language: 'php',
        content: buildPreview(),
      },
    });
  };

  const actions = [
    {
      value: 'Edit',
      onClick: item => {
        setUpdateMode(true);
        handleItemEdit(item);
      },
    },
    {
      value: 'Remove',
      onClick: item => setDefines(prev => ArrayHelper.filterOut(prev, 'slug', item.slug)),
    },
  ];

  const headers = [
    JsxHelper.createTableTextHeader('name', 'Constant', '40%'),
    JsxHelper.createTableTextHeader('value', 'Value', '40%'),
    JsxHelper.createTableActionsHeader(actions, '20%'),
  ];

  const breadcrumbs = JsxHelper.createBreadcrumbs('WP Config', 'settings');

  return (
    <Container className={isGlobalPage ? 'margin-0' : 'margin-24'}>
      <TitleBar className={isGlobalPage ? '' : 'titlebar padding-0'}>
        <TitleBar.Title breadcrumbs={isGlobalPage ? breadcrumbs : null}>WP Config</TitleBar.Title>
        { isGlobalPage && 
          <TitleBar.Actions>
          {JsxHelper.createBackButton()}
          </TitleBar.Actions>
        }
      </TitleBar>
      {!isGlobalPage && (
        <p className='color-primary subheader margin-bottom-30 padding-left-0'>
          Add custom entries that will automatically add inside the wp-config.php file. This has been disabled from the File Manager for security reasons.
        </p>
      )}
      <div className={isGlobalPage ? 'custom-table-margin' : ''}>
        <WPSDataTable
          columns={headers}
          body={defines}
          rowsPerPage={100}
        />
      </div>
      <Row className={(isGlobalPage ? 'margin-24' : 'margin-bottom-30') + ' action-buttons'}>
        <WPSButton
          className='add-btn-info'
          onClick={() => setModal(true)}>
          Add
        </WPSButton>
        {!isEmpty(defines) && (
          <WPSButton
            className='preview--btn'
            onClick={handlePreviewFileContent}>
            Preview
          </WPSButton>
        )}
        <WPSButton
          className='save--btn'
          onClick={onSubmit}
          loading={loading}>
          Save
        </WPSButton>
      </Row>
      {modal && (
        <Modal
          className='input-modal'
          icon={updateMode ? 'edit' : 'create'}
          iconColor={'success'}
          title={updateMode ? 'Edit PHP Constant' : 'Add PHP Constant'}
          onClose={() => {
            setModal(false);
            setUpdateMode(false);
          }}
          onConfirm={handleSubmit(updateMode ? onEdit : onAdd)}
          confirmBtn={updateMode ? 'Edit' : 'Add'}>
          <WPSForm onSubmit={e => e.preventDefault()}>
            <WPSLabel required>
              Name
              {errors.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
            </WPSLabel>
            <WPSInput
              type='text'
              name='name'
              placeholder='e.g. DISABLE_WP_CRON'
              value={details.name}
              onChange={onChange}
              ref={register({
                required: FormHelper.messages.required,
                pattern: {
                  value: FormHelper.regex.wpDefine,
                  message: FormHelper.messages.invalidWPDefine,
                },
              })}
            />
            <WPSLabel required>
              Value
              {errors.value && <ErrorMsg>{errors.value.message}</ErrorMsg>}
            </WPSLabel>
            <WPSInput
              type='text'
              name='value'
              placeholder='e.g. true'
              value={details.value}
              onChange={onChange}
              ref={register({ required: FormHelper.messages.required })}
            />
            <WPSLabel>
              Preview
            </WPSLabel>
            <div className='primary-font-color'>define( '{details.name}', {['true', 'false'].includes(details.value) ? details.value : `'${details.value}'`} );</div>
          </WPSForm>
        </Modal>
      )}
    </Container>
  );
};

export default WPDefines;
